import axios from 'axios';
import { IDatabase } from '../../../database';
import { IFlowyAssociation, IRealmFormGizmo, IUserV2 } from '../../../types';

interface IParams {
  pingResponseData: {
    forms: [
      {
        id: string;
        version: string;
        associations?: IFlowyAssociation[];
      }
    ];
  };
  savedForms: IRealmFormGizmo[];
  allRegistersUploaded?: boolean;
  context: {
    apiUrl: string;
    user: IUserV2;
    database: IDatabase;
  };
}

interface IOptionVersion {
  f_id: string;
  version: number;
}

/**
 * Compare the ping response associations with the current forms, if necessary download the latest
 * form options
 *
 * @param {any} pingResponse ping response from the server
 * @param {IRealmFormGizmo} savedForms latest version of root gizmos available locally
 * @returns
 */
async function syncFormOptions({
  pingResponseData,
  savedForms,
  allRegistersUploaded,
  context,
}: IParams) {
  const { apiUrl, user, database } = context;

  if (allRegistersUploaded && user?.credentials) {
    const credentials: any = user.credentials;
    const pingUrl = `${apiUrl}/main_api/v1/ping/`;

    try {
      const response = await axios.post(
        pingUrl,
        {},
        { headers: { ...credentials } }
      );
      if (response.status === 200) {
        pingResponseData = response.data;
      }
    } catch (e) {}
  }

  for (const pingForm of pingResponseData.forms) {
    if (pingForm.associations) {
      // If the ping form has child association we'll check if it has changed in comparisson to
      // the saved one
      const childAssociations = pingForm.associations.filter(
        a => a.relationship === 'child'
      );

      if (childAssociations.length > 0) {
        const savedForm = savedForms.find(sf => sf.id === pingForm.id);

        let formUpdated = false;
        if (savedForm) {
          const form = savedForm.formGizmo;
          const versionsParams: {
            formId: string;
            gizmoFormId: string;
            fid: string;
            originalFid: string;
            originalFormId: string;
            catalogFid: string;
          }[] = [];

          if (form.associations) {
            for (const childAssociation of childAssociations) {
              const association = form.associations.find(
                a => a.rootGizmoFid === childAssociation.rootGizmoFid
              );

              if (
                association &&
                JSON.stringify(association.features) !==
                  JSON.stringify(childAssociation.features)
              ) {
                // This is done in order to update the features in the local configuration, with
                // this we're not going to ask for the catalogs at every sync process
                association.features = childAssociation.features;

                const gizmo = form.questions.find(
                  // q => q.form_id === association.rootGizmoFid
                  q =>
                    q.options?.selectField?.formId === association.rootGizmoFid
                );

                // if (
                //   gizmo &&
                //   gizmo.catalog &&
                //   gizmo.original_fid &&
                //   gizmo.original_form_id &&
                //   gizmo.form_id
                // ) {
                //   versionsParams.push({
                //     formId: form.id,
                //     gizmoFormId: gizmo.form_id,
                //     fid: gizmo.f_id,
                //     originalFid: gizmo.original_fid,
                //     originalFormId: gizmo.original_form_id,
                //     catalogFid: gizmo.catalog,
                //   });
                // }
                if (
                  gizmo?.options?.selectField?.catalogFid &&
                  gizmo?.originalFid &&
                  gizmo.originalFormId &&
                  gizmo?.options.selectField.formId
                ) {
                  versionsParams.push({
                    formId: form.id,
                    gizmoFormId: gizmo.options.selectField.formId,
                    fid: gizmo.f_id,
                    originalFid: gizmo.originalFid,
                    originalFormId: gizmo.originalFormId,
                    catalogFid: gizmo.options.selectField.catalogFid,
                  });
                }
              } else if (!association) {
                form.associations.push(childAssociation);
                const gizmo = form.questions.find(
                  // q => q.form_id === childAssociation.rootGizmoFid
                  q =>
                    q.options?.selectField?.formId ===
                    childAssociation.rootGizmoFid
                );

                // if (
                //   gizmo &&
                //   gizmo.catalog &&
                //   gizmo.original_fid &&
                //   gizmo.form_id &&
                //   gizmo.original_form_id
                // ) {
                //   versionsParams.push({
                //     formId: form.id,
                //     gizmoFormId: gizmo.form_id,
                //     fid: gizmo.f_id,
                //     originalFid: gizmo.original_fid,
                //     originalFormId: gizmo.original_form_id,
                //     catalogFid: gizmo.catalog,
                //   });
                // }
                if (
                  gizmo?.options?.selectField?.catalogFid &&
                  gizmo.originalFid &&
                  gizmo.originalFormId &&
                  gizmo.options.selectField.formId
                ) {
                  versionsParams.push({
                    formId: form.id,
                    gizmoFormId: gizmo.options.selectField.formId,
                    fid: gizmo.f_id,
                    originalFid: gizmo.originalFid,
                    originalFormId: gizmo.originalFormId,
                    catalogFid: gizmo.options.selectField.catalogFid,
                  });
                }
              }
            }
          }

          if (versionsParams.length > 0 && form.catalogs && user?.credentials) {
            const credentials: any = user.credentials;

            for (const versionParams of versionsParams) {
              const optionIdsToDownload: string[] = [];
              let opIdsNotInServer: string[] = [];
              const catalog = form.catalogs.find(
                c => c.f_id === versionParams.catalogFid
              );
              const gizmo = form.questions.find(
                q => q.f_id === versionParams.fid
              );

              const versionsResponse = await axios.get(
                `${apiUrl}/v3/forms/${versionParams.formId}/form-options/${versionParams.originalFid}/versions?externalFormId=${versionParams.originalFormId}`,
                {
                  headers: {
                    ...credentials,
                  },
                }
              );

              if (
                versionsResponse.status === 200 &&
                catalog &&
                catalog.options
              ) {
                const { data } = versionsResponse;

                if (
                  data &&
                  data.options &&
                  data.options.versions &&
                  Array.isArray(catalog.options)
                ) {
                  const optionVersions: IOptionVersion[] =
                    data.options.versions;

                  // We're finding the option ids that are no longer present on the server
                  opIdsNotInServer = catalog.options
                    .filter(
                      co => !optionVersions.map(o => o.f_id).includes(co.f_id)
                    )
                    .map(co => co.f_id);

                  for (const optionVersion of optionVersions) {
                    const option = catalog.options.find(
                      o => o.f_id === optionVersion.f_id
                    );

                    if (option) {
                      if (option.version !== optionVersion.version) {
                        optionIdsToDownload.push(optionVersion.f_id);
                      }
                    } else {
                      optionIdsToDownload.push(optionVersion.f_id);
                    }
                  }
                } else if (
                  data &&
                  data.options &&
                  data.options.versions &&
                  !Array.isArray(catalog.options)
                ) {
                  const optionVersions: IOptionVersion[] =
                    data.options.versions;
                  const optionVersionsFids = optionVersions.map(o => o.f_id);
                  const optionFidsPresentLocally: string[] = [];

                  for (const parentCatalogId in catalog.options) {
                    for (const option of catalog.options[parentCatalogId]) {
                      if (!optionVersionsFids.includes(option.f_id)) {
                        // If an option present locally but didn't arrive on the optionFids
                        // from the server we're going to remove it from the local database
                        opIdsNotInServer.push(option.f_id);
                      } else {
                        // If the local option exists on the optionFids arriving from the server
                        // we're going to check if the version is different, if it's different
                        // we add it to the array of option fids that will be downloaded
                        const optionVersion = optionVersions.find(
                          o => o.f_id === option.f_id
                        );

                        if (optionVersion) {
                          optionFidsPresentLocally.push(option.f_id);

                          if (option.version !== optionVersion.version) {
                            optionIdsToDownload.push(optionVersion.f_id);
                          }
                        }
                      }
                    }
                  }

                  for (const optionFid of optionVersionsFids.filter(
                    o => !optionFidsPresentLocally.includes(o)
                  )) {
                    // If an optionFid is not present locally we have to download it
                    optionIdsToDownload.push(optionFid);
                  }
                }
              }

              console.log('--------------> optionIdsToDownload: ', optionIdsToDownload);
              if (optionIdsToDownload.length > 0 && catalog?.options && gizmo) {
                const optionsResponse = await axios.post(
                  `${apiUrl}/v3/forms/${versionParams.formId}/form-options/${versionParams.originalFid}/revisions?externalFormId=${versionParams.originalFormId}`,
                  {
                    optionIds: optionIdsToDownload,
                  },
                  {
                    headers: {
                      ...credentials,
                    },
                  }
                );

                if (optionsResponse.status === 200) {
                  const { data } = optionsResponse;

                  if (
                    gizmo.type === 'options' &&
                    Array.isArray(catalog.options)
                  ) {
                    let newOptions = catalog.options.map(o => {
                      const newOption = data.options.find(
                        (op: any) => op.f_id === o.f_id
                      );

                      if (newOption) {
                        return newOption;
                      } else {
                        return o;
                      }
                    });

                    const notPresent = data.options.filter(
                      (op: any) =>
                        !newOptions.map(o => o.f_id).includes(op.f_id)
                    );
                    if (notPresent.length > 0) {
                      newOptions = newOptions.concat(notPresent);
                    }

                    catalog.options = newOptions;

                    formUpdated = true;
                  } else if (
                    gizmo.type === 'options_dependant' &&
                    !Array.isArray(catalog.options)
                  ) {
                    for (const option of data.options) {
                      if (option.parentFormId && option.parentOptionFid) {
                        // const parentFilledFormId = option.parentOptionFid;
                        const parentFilledFormId = option.parentOptionId;

                        if (parentFilledFormId) {
                          if (!catalog.options[parentFilledFormId]) {
                            catalog.options[parentFilledFormId] = [];
                          }

                          const present = catalog.options[
                            parentFilledFormId
                          ].find(o => o.f_id === option.f_id);

                          // First we assign the options to a variable
                          let newOptions = catalog.options[parentFilledFormId];

                          // If the option is already present on the already present options
                          // we're going to substitute the option with the one arriving from
                          // the server
                          if (present) {
                            newOptions = catalog.options[
                              parentFilledFormId
                            ].map(o => {
                              const newOption = data.options.find(
                                (op: any) => op.f_id === o.f_id
                              );

                              if (newOption) {
                                return newOption;
                              } else {
                                return o;
                              }
                            });
                          } else {
                            // If not present we add it
                            newOptions.push(option);
                          }

                          catalog.options[parentFilledFormId] = newOptions;

                          formUpdated = true;

                          // const notPresent = data.options.filter(
                          //   (op: any) =>
                          //     !newOptions.map(o => o.f_id).includes(op.f_id)
                          // );
                          // if (notPresent.length > 0) {
                          //   newOptions = newOptions.concat(notPresent);
                          // }

                          // console.log('************nOps: ', newOptions);
                          // console.log('nOps.j: ', JSON.stringify(newOptions));
                        }
                      }
                    }
                  }
                }
              }

              // If we have options present locally but that are no longer on the server we're
              // going to filter (remove) them
              if (
                opIdsNotInServer.length > 0 &&
                catalog?.options &&
                Array.isArray(catalog.options)
              ) {
                catalog.options = catalog.options.filter(
                  // If the option is not in the server it must go
                  option => !opIdsNotInServer.includes(option.f_id)
                );

                formUpdated = true;
              } else if (
                opIdsNotInServer.length > 0 &&
                catalog?.options &&
                !Array.isArray(catalog.options)
              ) {
                for (const parentFilledFormId in catalog.options) {
                  catalog.options[parentFilledFormId] = catalog.options[
                    parentFilledFormId
                  ].filter(
                    // If the option is not in the server it must go
                    option => !opIdsNotInServer.includes(option.f_id)
                  );

                  formUpdated = true;
                }
              }
            }
          }
        }

        if (formUpdated && savedForm) {
          await database.set({
            type: 'forms',
            value: savedForm.formGizmo,
            isMainForm: savedForm.isMain,
            update: true,
          });
        }
      }
    }
  }
}

export default syncFormOptions;
